/**
 * MegaDetail Page - Complete Mega Evolution information
 */
import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronLeft, ChevronRight, Heart, ArrowLeft, 
  Ruler, Weight, Sparkles, Zap, TrendingUp, ArrowRight
} from 'lucide-react';
import { usePokemon } from '../hooks/usePokemon';
import { useApp } from '../context/AppContext';
import { TypeBadgeGroup } from '../components/pokedex/TypeBadge';
import { TypeCalculator } from '../components/pokedex/TypeCalculator';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { MEGA_EVOLUTIONS, LEGENDS_ZA_SPRITES, getMegaSpriteUrl, getPokeApiMegaSpriteId } from '../data/megaEvolutions';
import { formatAbilityName } from '../data/pokemonTranslations';
import { cn } from '../lib/utils';

// Stat colors
const STAT_COLORS = {
  hp: 'bg-red-500',
  attack: 'bg-orange-500',
  defense: 'bg-yellow-500',
  spAtk: 'bg-blue-500',
  spDef: 'bg-green-500',
  speed: 'bg-pink-500'
};

const STAT_NAMES = {
  hp: { en: 'HP', es: 'PS' },
  attack: { en: 'Attack', es: 'Ataque' },
  defense: { en: 'Defense', es: 'Defensa' },
  spAtk: { en: 'Sp. Atk', es: 'At. Esp.' },
  spDef: { en: 'Sp. Def', es: 'Def. Esp.' },
  speed: { en: 'Speed', es: 'Velocidad' }
};

// Stat bar component with comparison
function MegaStatBar({ name, value, baseValue, maxValue = 255, language }) {
  const percentage = (value / maxValue) * 100;
  const diff = value - baseValue;
  const label = STAT_NAMES[name]?.[language] || name;
  
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground w-16 sm:w-20 truncate">{label}</span>
      <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className={cn('h-full rounded-full', STAT_COLORS[name] || 'bg-violet-500')}
        />
      </div>
      <span className="text-sm font-mono w-8 text-right font-semibold">{value}</span>
      {diff !== 0 && (
        <span className={cn(
          'text-xs font-mono w-10 font-semibold',
          diff > 0 ? 'text-green-500' : 'text-red-500'
        )}>
          {diff > 0 ? '+' : ''}{diff}
        </span>
      )}
    </div>
  );
}

export default function MegaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, isFavorite, toggleFavorite, language } = useApp();
  const { getPokemon, getPokemonSpecies, getAbility, getLocalizedName, getLocalizedFlavorText, getGeneration } = usePokemon();
  
  const [basePokemon, setBasePokemon] = useState(null);
  const [baseSpecies, setBaseSpecies] = useState(null);
  const [megaAbilityData, setMegaAbilityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const megaId = parseInt(id);
  
  // Find the mega evolution data
  const megaData = useMemo(() => {
    return MEGA_EVOLUTIONS.find(m => m.id === megaId);
  }, [megaId]);
  
  // Get all megas for navigation
  const allMegas = useMemo(() => MEGA_EVOLUTIONS, []);
  const currentIndex = allMegas.findIndex(m => m.id === megaId);
  
  const favorite = isFavorite(megaId);
  
  // Fetch base pokemon data for comparison
  useEffect(() => {
    let mounted = true;
    
    const fetchData = async () => {
      if (!megaData) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      setImageLoaded(false);
      
      const abilityApiName = megaData.ability.toLowerCase().replace(/\s+/g, '-');
      const [pokemonData, speciesData, abilityInfo] = await Promise.all([
        getPokemon(megaData.baseId),
        getPokemonSpecies(megaData.baseId),
        getAbility(abilityApiName)
      ]);

      if (mounted) {
        setBasePokemon(pokemonData);
        setBaseSpecies(speciesData);
        setMegaAbilityData(abilityInfo);
        setLoading(false);
      }
    };
    
    fetchData();
    return () => { mounted = false; };
  }, [megaData, getPokemon, getPokemonSpecies, getAbility]);
  
  // Get sprite URL using the helper function
  const spriteUrl = useMemo(() => {
    if (!megaData) return '';
    return getMegaSpriteUrl(megaData);
  }, [megaData]);

  const fallbackSpriteUrls = useMemo(() => {
    if (!megaData) return [];

    const megaSpriteId = megaData.apiName ? getPokeApiMegaSpriteId(megaData.apiName) : null;
    const urls = [
      megaSpriteId ? `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${megaSpriteId}.png` : null,
      megaSpriteId ? `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${megaSpriteId}.png` : null,
      `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${megaData.baseId}.png`,
      `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${megaData.baseId}.png`
    ].filter(Boolean);

    return urls.filter((url, index) => urls.indexOf(url) === index);
  }, [megaData]);
  
  // Calculate base stats from API data
  const baseStats = useMemo(() => {
    if (!basePokemon) return null;
    const stats = basePokemon.stats || [];
    return {
      hp: stats.find(s => s.stat.name === 'hp')?.base_stat || 0,
      attack: stats.find(s => s.stat.name === 'attack')?.base_stat || 0,
      defense: stats.find(s => s.stat.name === 'defense')?.base_stat || 0,
      spAtk: stats.find(s => s.stat.name === 'special-attack')?.base_stat || 0,
      spDef: stats.find(s => s.stat.name === 'special-defense')?.base_stat || 0,
      speed: stats.find(s => s.stat.name === 'speed')?.base_stat || 0
    };
  }, [basePokemon]);
  
  const baseBst = baseStats ? Object.values(baseStats).reduce((a, b) => a + b, 0) : 0;
  
  // Get localized ability name. Custom Pokémon Champions abilities are not in
  // PokeAPI, so fall back to the local translation map (matched by slug).
  const abilitySlug = megaData?.ability?.toLowerCase().replace(/\s+/g, '-');
  const localizedAbilityName = megaAbilityData
    ? (getLocalizedName(megaAbilityData.names) || formatAbilityName(abilitySlug, language))
    : formatAbilityName(abilitySlug, language);

  // Get localized data
  const localizedBaseName = baseSpecies ? getLocalizedName(baseSpecies.names) : megaData?.baseName;
  const description = baseSpecies ? getLocalizedFlavorText(baseSpecies.flavor_text_entries) : '';
  const generation = megaData ? getGeneration(megaData.baseId) : 1;
  
  // Navigation
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < allMegas.length - 1;
  
  const goToPrevious = () => {
    if (hasPrevious) navigate(`/mega/${allMegas[currentIndex - 1].id}`);
  };
  
  const goToNext = () => {
    if (hasNext) navigate(`/mega/${allMegas[currentIndex + 1].id}`);
  };
  
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex]);
  
  // Texts
  const texts = {
    megaStone: language === 'es' ? 'Mega Piedra' : 'Mega Stone',
    ability: language === 'es' ? 'Habilidad' : 'Ability',
    stats: language === 'es' ? 'Estadísticas' : 'Stats',
    comparison: language === 'es' ? 'Comparación con forma base' : 'Comparison with base form',
    total: language === 'es' ? 'Total' : 'Total',
    viewBase: language === 'es' ? 'Ver Pokémon base' : 'View base Pokémon',
    weakRes: language === 'es' ? 'Debilidades y Resistencias' : 'Weaknesses & Resistances',
    newMega: language === 'es' ? 'Nueva Mega de Legends Z-A' : 'New Mega from Legends Z-A',
    typeChange: language === 'es' ? 'Cambio de tipo' : 'Type change'
  };
  
  if (loading) {
    return <MegaDetailSkeleton />;
  }
  
  if (!megaData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-muted-foreground mb-4">{t('error')}</p>
          <Button onClick={() => navigate('/')}>{t('home')}</Button>
        </div>
      </div>
    );
  }
  
  // Check if types changed from base
  const baseTypes = basePokemon?.types?.map(t => t.type.name) || [];
  const typesChanged = JSON.stringify(megaData.types) !== JSON.stringify(baseTypes);
  
  return (
    <div className="min-h-screen" data-testid="mega-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Back button and navigation */}
        <div className="flex items-center justify-between mb-6">
          <Link to="/">
            <Button variant="ghost" size="sm" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('home')}
            </Button>
          </Link>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={goToPrevious}
              disabled={!hasPrevious}
              data-testid="prev-mega-btn"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <span className="text-sm text-muted-foreground min-w-[80px] text-center flex items-center justify-center gap-1">
              <Zap className="w-3 h-3 text-yellow-500" />
              {currentIndex + 1}/{allMegas.length}
            </span>
            <Button
              variant="outline"
              size="icon"
              onClick={goToNext}
              disabled={!hasNext}
              data-testid="next-mega-btn"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
        
        {/* Main content */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Left column - Image and basic info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-5"
          >
            <div className="bg-card rounded-3xl border-2 border-yellow-500/50 shadow-lg overflow-hidden">
              {/* Mega badge header */}
              <div className="bg-gradient-to-r from-violet-600/20 to-rose-500/20 py-2 px-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-500" />
                  <span className="text-sm font-bold text-yellow-600 dark:text-yellow-400 uppercase tracking-wider">MEGA EVOLUTION</span>
                </div>
                {!megaData.inApi && (
                  <span className="text-xs bg-violet-500/20 text-violet-600 dark:text-violet-400 px-2 py-0.5 rounded-full">
                    {texts.newMega}
                  </span>
                )}
              </div>
              
              {/* Image section */}
              <div className="relative bg-gradient-to-br from-yellow-100 to-orange-100 dark:from-yellow-900/20 dark:to-orange-900/20 p-8">
                {/* ID watermark */}
                <span className="absolute top-4 left-4 text-6xl font-black text-black/5 dark:text-white/5 select-none">
                  M#{String(megaData.baseId).padStart(3, '0')}
                </span>
                
                {/* Favorite button */}
                <button
                  onClick={() => toggleFavorite(megaId)}
                  data-testid="mega-detail-favorite-btn"
                  className="absolute top-4 right-4 p-3 rounded-full bg-white/80 dark:bg-black/50 backdrop-blur-sm hover:scale-110 transition-transform z-10"
                >
                  <Heart 
                    className={`w-6 h-6 transition-colors ${
                      favorite 
                        ? 'fill-rose-500 text-rose-500' 
                        : 'text-muted-foreground hover:text-rose-500'
                    }`}
                  />
                </button>
                
                {/* Pokemon image */}
                <div className="relative flex items-center justify-center h-64 sm:h-80">
                  {!imageLoaded && (
                    <Skeleton className="absolute w-48 h-48 rounded-full" />
                  )}
                  <AnimatePresence mode="wait">
                    <motion.div className="relative">
                      <motion.img
                        src={spriteUrl}
                        alt={megaData.name}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: imageLoaded ? 1 : 0, scale: 1 }}
                        transition={{ duration: 0.3 }}
                        onLoad={() => setImageLoaded(true)}
                        onError={(e) => {
                          const img = e.currentTarget;
                          const nextFallback = fallbackSpriteUrls.find(url => url !== img.src);

                          if (nextFallback) {
                            img.src = nextFallback;
                            return;
                          }

                          setImageLoaded(true);
                        }}
                        className="w-48 h-48 sm:w-64 sm:h-64 object-contain drop-shadow-2xl"
                      />
                      {/* Mega glow effect */}
                      <div className="absolute inset-0 bg-gradient-to-t from-yellow-500/30 to-transparent rounded-full blur-2xl -z-10" />
                      <Sparkles className="absolute -top-2 -right-2 w-8 h-8 text-yellow-500" />
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>
              
              {/* Basic info */}
              <div className="p-6 space-y-4">
                <div>
                  <p className="text-sm font-bold text-yellow-600 dark:text-yellow-400 uppercase tracking-widest mb-1 flex items-center gap-1">
                    <Zap className="w-3 h-3" />
                    #{String(megaData.baseId).padStart(3, '0')} • {t('generation')} {generation}
                  </p>
                  <h1 className="text-3xl sm:text-4xl font-bold text-foreground">
                    {megaData.name}
                  </h1>
                </div>
                
                {/* Types with change indicator */}
                <div className="space-y-2">
                  <TypeBadgeGroup types={megaData.types} size="md" />
                  {typesChanged && (
                    <p className="text-xs text-yellow-600 dark:text-yellow-400 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      {texts.typeChange}: {baseTypes.join('/')} → {megaData.types.join('/')}
                    </p>
                  )}
                </div>
                
                {/* Mega Stone */}
                <div className="p-3 bg-yellow-500/10 rounded-xl border border-yellow-500/30">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{texts.megaStone}</p>
                  <p className="font-semibold text-yellow-600 dark:text-yellow-400">{megaData.stone}</p>
                </div>
                
                {/* Ability */}
                <div className="p-3 bg-muted/50 rounded-xl">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{texts.ability}</p>
                  <p className="font-semibold text-foreground capitalize">{localizedAbilityName?.replace(/-/g, ' ')}</p>
                </div>
                
                {/* Description from base Pokemon */}
                {description && (
                  <div className="pt-2">
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {description}
                    </p>
                  </div>
                )}
                
                {/* View base Pokemon link */}
                <Link to={`/pokemon/${megaData.baseId}`}>
                  <Button variant="outline" className="w-full mt-2" data-testid="view-base-btn">
                    <ArrowRight className="w-4 h-4 mr-2" />
                    {texts.viewBase} ({localizedBaseName})
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
          
          {/* Right column - Stats and comparison */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-7 space-y-6"
          >
            {/* Stats Panel */}
            <div className="bg-card rounded-3xl border border-border shadow-lg p-4 sm:p-6">
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-violet-500" />
                {texts.stats}
              </h2>
              
              <p className="text-sm text-muted-foreground mb-4">{texts.comparison}</p>
              
              <div className="space-y-3">
                {Object.entries(megaData.stats).map(([statName, value]) => (
                  <MegaStatBar
                    key={statName}
                    name={statName}
                    value={value}
                    baseValue={baseStats?.[statName] || 0}
                    language={language}
                  />
                ))}
              </div>
              
              {/* Total BST */}
              <div className="mt-6 pt-4 border-t border-border">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-foreground">{texts.total}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-muted-foreground">{baseBst}</span>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    <span className="font-bold text-xl text-foreground">{megaData.bst}</span>
                    <span className="text-green-500 font-semibold flex items-center gap-1">
                      <TrendingUp className="w-4 h-4" />
                      +{megaData.bst - baseBst}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Type Calculator */}
            <div className="bg-card rounded-3xl border border-border shadow-lg p-4 sm:p-6">
              <h2 className="text-xl font-bold text-foreground mb-4">
                {texts.weakRes}
              </h2>
              <TypeCalculator types={megaData.types} />
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function MegaDetailSkeleton() {
  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-6">
          <Skeleton className="h-9 w-24" />
          <div className="flex items-center gap-2">
            <Skeleton className="h-9 w-9" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-9 w-9" />
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          <div className="lg:col-span-5">
            <div className="bg-card rounded-3xl border-2 border-yellow-500/30 overflow-hidden">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-80 w-full" />
              <div className="p-6 space-y-4">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-10 w-48" />
                <div className="flex gap-2">
                  <Skeleton className="h-7 w-20 rounded-full" />
                  <Skeleton className="h-7 w-20 rounded-full" />
                </div>
                <Skeleton className="h-16 w-full rounded-xl" />
                <Skeleton className="h-16 w-full rounded-xl" />
              </div>
            </div>
          </div>
          
          <div className="lg:col-span-7 space-y-6">
            <div className="bg-card rounded-3xl border border-border p-6">
              <Skeleton className="h-8 w-32 mb-4" />
              <div className="space-y-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-6 w-full" />
                ))}
              </div>
            </div>
            <div className="bg-card rounded-3xl border border-border p-6">
              <Skeleton className="h-8 w-48 mb-4" />
              <Skeleton className="h-32 w-full" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
